import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

import { EMBEDDED_MENU_SOURCE, MENU_SHOW_MORE_ACTION_SUBJECT_ID } from './analytics.ts';
import { ConnectionsByLineExpandableLinkSection } from './ConnectionsByLineExpandableLinkSection.tsx';
import {
	getMenuShownAttributes,
	getUnauthenticatedLinkCount,
	type RefreshLinkUrlHandler,
	relatedContentHeadingMessage,
} from './ConnectionsByLineMenu.tsx';
import type { LinkDirection } from './ConnectionsByLineMenuItem.tsx';
import {
	DEFAULT_VISIBLE_LINK_COUNT,
	getTopConnectionLinks,
} from './utils/getTopConnectionLinks.ts';
import { groupSmartLinksByProduct } from './utils/groupSmartLinksByProduct.ts';
import type { EnhancedSmartLink } from './utils/smartLinkTypes.ts';
import { useTwgAnalyticsEvents } from './useTwgAnalyticsEvents.ts';
import {
	showLessMessage,
	showMoreMessage,
} from './teamwork-graph-preview-panel/pressable-group/ShowMoreRow.tsx';

const MENU_WIDTH = '320px';
const MENU_BODY_MAX_HEIGHT = '600px';

const i18n = defineMessages({
	menuLabel: {
		id: 'byline-connections.embedded-menu.label.do-not-translate',
		defaultMessage: 'Teamwork Graph',
		description:
			'Accessible name of the connections byline menu when Confluence is embedded in another product, where the menu has no visible title. "Teamwork Graph" is a product name and is intentionally not translated.',
	},
	linksTo: {
		id: 'byline-connections.embedded-menu.section.links-to',
		defaultMessage: 'Links to ({count})',
		description:
			'Heading of the links section of the connections byline menu when Confluence is embedded in another product, followed by the number of links found on the current page in parentheses.',
	},
	empty: {
		id: 'byline-connections.embedded-menu.empty',
		defaultMessage: 'This content doesn’t have any related content or links yet.',
		description:
			'Message shown in the connections byline menu when Confluence is embedded in another product and the current page has no related content and no links.',
	},
});

const styles = cssMap({
	menu: {
		width: MENU_WIDTH,
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.medium'),
	},
	content: {
		paddingBlock: token('space.100'),
		maxHeight: MENU_BODY_MAX_HEIGHT,
		overflowY: 'auto',
	},
	showMore: {
		paddingInline: token('space.150'),
		paddingBlockEnd: token('space.100'),
	},
	emptyState: {
		display: 'flex',
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		justifyContent: 'center',
	},
});

type EmbeddedSection = 'relatedContent' | 'linksTo';

type EmbeddedLinkSectionProps = {
	section: EmbeddedSection;
	heading: string;
	links: EnhancedSmartLink[];
	linkDirection: LinkDirection;
	compact?: boolean;
	refreshLinkUrl: RefreshLinkUrlHandler;
	onNavigate?: () => void;
};

// Shows the first few links of a section and toggles the rest inline. Embedded Confluence has no
// preview panel, so this is the only way to see more.
const EmbeddedLinkSection = ({
	section,
	heading,
	links,
	linkDirection,
	compact,
	refreshLinkUrl,
	onNavigate,
}: EmbeddedLinkSectionProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();
	const [isExpanded, setIsExpanded] = useState(false);
	const hasMore = links.length > DEFAULT_VISIBLE_LINK_COUNT;
	const visibleLinks = useMemo(
		() => (isExpanded ? links : links.slice(0, DEFAULT_VISIBLE_LINK_COUNT)),
		[isExpanded, links],
	);

	const handleToggle = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: MENU_SHOW_MORE_ACTION_SUBJECT_ID,
				source: EMBEDDED_MENU_SOURCE,
				attributes: { section, isExpanded: !isExpanded, linkCount: links.length },
			},
		}).fire();
		setIsExpanded((wasExpanded) => !wasExpanded);
	}, [createAnalyticsEvent, isExpanded, links.length, section]);

	if (links.length === 0) {
		return null;
	}

	return (
		<>
			<ConnectionsByLineExpandableLinkSection
				heading={heading}
				links={visibleLinks}
				refreshLinkUrl={refreshLinkUrl}
				linkDirection={linkDirection}
				onNavigate={onNavigate}
				compact={compact}
				analyticsSource={EMBEDDED_MENU_SOURCE}
			/>
			{hasMore ? (
				<Box xcss={styles.showMore}>
					{/* Toggles rather than disappearing, so keyboard focus is not dropped on expand. */}
					<Button
						appearance="subtle"
						spacing="compact"
						onClick={handleToggle}
						aria-expanded={isExpanded}
						testId={`connections-byline-embedded-menu.${section}.show-more-button`}
					>
						<Text weight="regular" color="color.text.subtlest">
							{formatMessage(isExpanded ? showLessMessage : showMoreMessage)}
						</Text>
					</Button>
				</Box>
			) : null}
		</>
	);
};

export type EmbeddedConnectionsByLineMenuProps = {
	links?: EnhancedSmartLink[];
	relatedContentLinks?: EnhancedSmartLink[];
	relatedContentLoading?: boolean;
	refreshLinkUrl: RefreshLinkUrlHandler;
	onNavigate?: () => void;
};

/**
 * The connections byline menu for embedded Confluence (e.g. a page inside Jira), gated by
 * `confluence_1p_and_3p_connection_byline_experiment` or `migrate_planner_to_docs_tab`. It lists
 * only Related content and Links to, each expanding inline, because embedded Confluence cannot
 * open the Teamwork Graph preview panel.
 */
export const EmbeddedConnectionsByLineMenu = ({
	links,
	relatedContentLinks,
	relatedContentLoading = false,
	refreshLinkUrl,
	onNavigate,
}: EmbeddedConnectionsByLineMenuProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();
	const relatedLinks = useMemo(() => relatedContentLinks ?? [], [relatedContentLinks]);
	// Same order as the preview panel's Links to: unconnected first, then document order.
	const linksToLinks = useMemo(
		() => getTopConnectionLinks(links ?? [], links?.length ?? 0),
		[links],
	);
	const initiallyVisibleLinks = useMemo(
		() => [
			...relatedLinks.slice(0, DEFAULT_VISIBLE_LINK_COUNT),
			...linksToLinks.slice(0, DEFAULT_VISIBLE_LINK_COUNT),
		],
		[relatedLinks, linksToLinks],
	);
	const menuShownAttributes = useMemo(
		() => getMenuShownAttributes(groupSmartLinksByProduct(initiallyVisibleLinks)),
		[initiallyVisibleLinks],
	);
	const unauthenticatedLinkCount = useMemo(
		() => getUnauthenticatedLinkCount(initiallyVisibleLinks),
		[initiallyVisibleLinks],
	);
	const hasFiredRef = useRef(false);
	const hasFiredUnauthenticatedRef = useRef(false);
	const isEmpty = relatedLinks.length === 0 && linksToLinks.length === 0;

	useEffect(() => {
		if (hasFiredRef.current) {
			return;
		}
		hasFiredRef.current = true;
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'viewed',
				actionSubject: 'menu',
				actionSubjectId: 'connectionsBylineMenu',
				source: EMBEDDED_MENU_SOURCE,
				attributes: menuShownAttributes,
			},
		}).fire();
	}, [createAnalyticsEvent, menuShownAttributes]);

	useEffect(() => {
		if (hasFiredUnauthenticatedRef.current || unauthenticatedLinkCount === 0) {
			return;
		}
		hasFiredUnauthenticatedRef.current = true;
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'viewed',
				actionSubject: 'unauthenticatedSmartlink',
				actionSubjectId: 'connectionsBylineMenu',
				source: EMBEDDED_MENU_SOURCE,
				attributes: { unauthenticatedLinkCount },
			},
		}).fire();
	}, [createAnalyticsEvent, unauthenticatedLinkCount]);

	return (
		<Box
			xcss={styles.menu}
			role="dialog"
			aria-label={formatMessage(i18n.menuLabel)}
			testId="connections-byline-embedded-menu"
		>
			<Box xcss={styles.content} testId="connections-byline-embedded-menu.content">
				<EmbeddedLinkSection
					section="relatedContent"
					// The embedded menu expands inline via Show more, so its heading always counts every row.
					heading={formatMessage(relatedContentHeadingMessage, {
						count: relatedLinks.length,
						isTruncated: 'false',
					})}
					links={relatedLinks}
					linkDirection="incoming"
					refreshLinkUrl={refreshLinkUrl}
					onNavigate={onNavigate}
				/>
				<EmbeddedLinkSection
					section="linksTo"
					heading={formatMessage(i18n.linksTo, { count: linksToLinks.length })}
					links={linksToLinks}
					linkDirection="outgoing"
					refreshLinkUrl={refreshLinkUrl}
					onNavigate={onNavigate}
					compact
				/>
				{isEmpty && relatedContentLoading ? (
					<Box xcss={styles.emptyState}>
						<Spinner size="medium" testId="connections-byline-embedded-menu.loading" />
					</Box>
				) : null}
				{isEmpty && !relatedContentLoading ? (
					<Box xcss={styles.emptyState} testId="connections-byline-embedded-menu.empty">
						<Text color="color.text.subtlest">{formatMessage(i18n.empty)}</Text>
					</Box>
				) : null}
			</Box>
		</Box>
	);
};
